import type { supervision } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure presentation + form logic for the Supervision screens.
 *
 * STATUS IS COMPUTED SERVER-SIDE, ALWAYS. Nothing here re-derives whether a
 * worker is compliant — that would risk a UI verdict disagreeing with the
 * engine that actually enforces it (`apps/api/src/domains/supervision/compliance.ts`).
 * This file only decides how a server-sent status READS: which pill colour,
 * which date format, which fields a form needs before it can submit.
 */

// --- Status presentation -----------------------------------------------------

/**
 * Pill colour per status. `overdue` and `no_supervisor` are both `urgent`: a
 * missed period is a real problem, and an unassigned supervisor is the
 * organization's problem to fix before the worker can possibly comply — either
 * way, it needs a human now. `current` is the only `good`.
 */
export const STATUS_VARIANT: Record<supervision.SupervisionStatus, Severity> = {
  current: 'good',
  due_soon: 'look',
  due: 'unsure',
  overdue: 'urgent',
  no_supervisor: 'urgent',
  not_applicable: 'plain',
  // NOT `plain`, and this is the whole point of the status existing. `plain` is
  // what `not_applicable` wears because nothing is owed and nobody need look.
  // `insufficient_context` means the opposite: nobody knows what is owed, and
  // the record has to be completed before anyone can. `unsure` is the variant
  // this design system already uses for "the answer is not established".
  insufficient_context: 'unsure',
};

export function statusPill(row: { status: supervision.SupervisionStatus; statusLabel: string }): {
  label: string;
  variant: Severity;
} {
  return { label: row.statusLabel, variant: STATUS_VARIANT[row.status] };
}

/** Tabs over the workforce list, `all` first. Order matters — worst first. */
export const STATUS_TABS: Array<{ key: 'all' | supervision.SupervisionStatus; label: string }> = [
  { key: 'all', label: 'Everyone' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'no_supervisor', label: 'No supervisor' },
  { key: 'due_soon', label: 'Due soon' },
  { key: 'due', label: 'Due' },
  { key: 'current', label: 'Current' },
  // Above `not_applicable`, below every real verdict: it needs a person, and
  // what it needs from them is a policy record rather than a supervision
  // session.
  { key: 'insufficient_context', label: 'Cannot be determined' },
  { key: 'not_applicable', label: 'Not applicable' },
];

/** "12 of 30 minutes" — null-safe, since a not-yet-applicable row has neither. */
export function minutesLabel(required: number | null, recorded: number | null): string {
  if (required === null) return '—';
  return `${recorded ?? 0} of ${required} minutes`;
}

/**
 * A `YYYY-MM-DD` organization-local calendar date, formatted for display
 * WITHOUT going through the viewer's own timezone — `new Date('2026-08-24')`
 * parses as UTC midnight, and a plain `toLocaleDateString()` would then render
 * in the browser's local zone, silently shifting the date backward for anyone
 * west of UTC. Pinning `timeZone: 'UTC'` on the formatter keeps the calendar
 * date the organization actually means.
 */
export function formatCalendarDate(date: string | null, locale?: string): string {
  if (!date) return '—';
  const d = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** A recorded instant (`occurred_at`), formatted in the viewer's own zone. */
export function formatWhen(iso: string | null, locale?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

export const FORMAT_LABELS: Record<
  NonNullable<supervision.RecordSessionInput['format']>,
  string
> = {
  in_person: 'In person',
  video: 'Video',
  phone: 'Phone',
  group: 'Group',
};

export const CADENCE_LABELS: Record<supervision.Cadence, string> = {
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

// --- Requirement draft --------------------------------------------------------

export interface RequirementDraft {
  name: string;
  description: string;
  cadence: supervision.Cadence;
  minutesRequired: string;
  sessionsRequired: string;
  appliesToRoleKey: string;
  appliesToProgramId: string;
  appliesToEmploymentStatus: '' | 'active' | 'inactive' | 'terminated';
  effectiveFrom: string;
  effectiveTo: string;
}

export const EMPTY_REQUIREMENT_DRAFT: RequirementDraft = {
  name: '',
  description: '',
  cadence: 'monthly',
  minutesRequired: '60',
  sessionsRequired: '1',
  appliesToRoleKey: '',
  appliesToProgramId: '',
  appliesToEmploymentStatus: '',
  effectiveFrom: '',
  effectiveTo: '',
};

export function draftFromRequirement(r: supervision.RequirementView): RequirementDraft {
  return {
    name: r.name,
    description: r.description ?? '',
    cadence: r.cadence,
    minutesRequired: String(r.minutesRequired),
    sessionsRequired: String(r.sessionsRequired),
    appliesToRoleKey: r.appliesToRoleKey ?? '',
    appliesToProgramId: r.appliesToProgramId ?? '',
    appliesToEmploymentStatus: (r.appliesToEmploymentStatus ??
      '') as RequirementDraft['appliesToEmploymentStatus'],
    effectiveFrom: '',
    effectiveTo: '',
  };
}

/**
 * Field-level errors for a requirement draft, keyed the way `SettingsField`
 * expects. `today` is a parameter (not `new Date()`) so "must be in the
 * future" is testable without mocking the clock.
 */
export function validateRequirementDraft(
  draft: RequirementDraft,
  today: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.name.trim().length === 0) errors['name'] = 'A name is required.';
  const minutes = Number(draft.minutesRequired);
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > 10_080) {
    errors['minutesRequired'] = 'Enter a whole number of minutes, 1–10,080.';
  }
  const sessions = draft.sessionsRequired.trim() === '' ? 1 : Number(draft.sessionsRequired);
  if (!Number.isInteger(sessions) || sessions < 1 || sessions > 100) {
    errors['sessionsRequired'] = 'Enter a whole number of sessions, 1–100.';
  }
  if (draft.effectiveFrom.trim() === '') {
    errors['effectiveFrom'] = 'Choose when this takes effect.';
  } else if (draft.effectiveFrom < today) {
    errors['effectiveFrom'] = 'This cannot take effect in the past.';
  }
  if (draft.effectiveTo && draft.effectiveTo < draft.effectiveFrom) {
    errors['effectiveTo'] = 'The end date cannot be before the start date.';
  }
  return errors;
}

export function requirementPayload(draft: RequirementDraft): supervision.CreateRequirementInput {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || undefined,
    cadence: draft.cadence,
    minutesRequired: Number(draft.minutesRequired),
    sessionsRequired: draft.sessionsRequired.trim() === '' ? 1 : Number(draft.sessionsRequired),
    appliesToRoleKey: draft.appliesToRoleKey.trim() || null,
    appliesToProgramId: draft.appliesToProgramId.trim() || null,
    appliesToEmploymentStatus: draft.appliesToEmploymentStatus || null,
    effectiveFrom: draft.effectiveFrom,
    effectiveTo: draft.effectiveTo.trim() || null,
  };
}

// --- Session draft -------------------------------------------------------------

export interface SessionDraft {
  occurredAt: string;
  durationMinutes: string;
  format: NonNullable<supervision.RecordSessionInput['format']>;
  requirementId: string;
}

export function emptySessionDraft(nowLocalInput: string): SessionDraft {
  return {
    occurredAt: nowLocalInput,
    durationMinutes: '30',
    format: 'in_person',
    requirementId: '',
  };
}

/**
 * The current moment as a `datetime-local` input value, in the VIEWER's own
 * local time — the picker itself is local, so the default it opens with has
 * to be too, or it would look wrong the instant it rendered.
 */
export function nowLocalInput(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/** An ISO instant, as the same `datetime-local` shape, for pre-filling a correction. */
export function toDatetimeLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return nowLocalInput(d);
}

/**
 * `nowIso` bounds the future-dated check client-side, mirroring the server's
 * own rule ("supervision cannot be recorded before it has happened") — but the
 * server checks again regardless, so this is only ever a courtesy.
 */
export function validateSessionDraft(draft: SessionDraft, nowIso: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.occurredAt.trim() === '') {
    errors['occurredAt'] = 'Choose when this happened.';
  } else {
    const occurred = new Date(draft.occurredAt);
    if (Number.isNaN(occurred.getTime())) {
      errors['occurredAt'] = 'That date and time is not valid.';
    } else if (occurred.getTime() > new Date(nowIso).getTime() + 60_000) {
      errors['occurredAt'] = 'Supervision cannot be recorded before it has happened.';
    }
  }
  const minutes = Number(draft.durationMinutes);
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
    errors['durationMinutes'] = 'Enter a whole number of minutes, 1–1,440.';
  }
  return errors;
}

export function sessionPayload(
  workerId: string,
  supervisorWorkerId: string,
  draft: SessionDraft,
): supervision.RecordSessionInput {
  return {
    workerId,
    supervisorWorkerId,
    requirementId: draft.requirementId || null,
    occurredAt: new Date(draft.occurredAt).toISOString(),
    durationMinutes: Number(draft.durationMinutes),
    format: draft.format,
  };
}

/** The server's message when it has one — supervision refusals are specific. */
export function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return 'That did not go through.';
}
